// askSmart-ui/src/pages/TOSP/portal/portalSummary.ts
import {
  NaturalBreakpointYear,
  occupancyBreakpointYear,
  Tenant,
  tenantLabel,
  TospConfiguration,
  Unit,
} from "../types";

// Which tenant record belongs to the person who just logged in.
//
// Matched on the tenant's primary contact email, which is the only link
// between an account and a tenant today. It's a stopgap: it ties the portal to
// one named contact per tenant, so a second person at the shop can't be given
// access without taking over that field. A proper account-to-tenant link
// belongs on the tenant record, and this function is where that change lands.
export function tenantForEmail(tenants: Tenant[], email: string | undefined): Tenant | null {
  return tenantsForEmail(tenants, email)[0] ?? null;
}

/**
 * EVERY tenancy this account holds, in name order.
 *
 * One person can be the contact for several businesses in the building — and
 * during onboarding the property manager holds each new tenancy herself until
 * the tenant has an account, so several at once is normal rather than rare.
 * The portal used to take the first match and silently ignore the rest, which
 * meant a second business simply didn't exist as far as its owner could tell.
 *
 * The server has always handled this: `callerTenantIds` returns an array, so
 * reading and reporting for several tenancies was already permitted. Only the
 * browser was picking one.
 */
export function tenantsForEmail(
  tenants: Tenant[],
  email: string | undefined,
  /** The signed-in account, for delegated tenancies. */
  userId?: string,
): Tenant[] {
  const wanted = (email || "").trim().toLowerCase();
  const account = (userId || "").trim();
  if (!wanted && !account) return [];
  return tenants
    .filter(
      (tenant) =>
        (wanted &&
          (tenant.primaryContactEmail || "").trim().toLowerCase() === wanted) ||
        // Or a tenancy they've been asked to act for. Matched on the account
        // rather than the address, so re-pointing an email can't hand somebody
        // else's delegation to a different person. This must agree with
        // `callerTenantIds` on the server — they are the same question asked
        // in two places, and a disagreement shows as a portal that lists a
        // business whose every request is refused.
        (account !== "" &&
          (tenant.delegates || []).some((row) => row.userId === account)),
    )
    .sort((a, b) => tenantLabel(a).localeCompare(tenantLabel(b)));
}

/** Whether this account is the tenancy's primary contact rather than a delegate. */
export const isPrimaryContact = (
  tenant: Tenant | null,
  email: string | undefined,
): boolean =>
  !!tenant &&
  !!email &&
  (tenant.primaryContactEmail || "").trim().toLowerCase() ===
    email.trim().toLowerCase();

/** One leased space's contribution to the tenant's breakpoint. */
export interface TenantBreakpointLine {
  /** The leased space this line is for. */
  spaceId: string;
  /** What the lease calls it, or the unit it points at. */
  label: string;
  breakdown: NaturalBreakpointYear;
}

export interface TenantBreakpoint {
  /** Only the spaces that actually carry a breakpoint. */
  lines: TenantBreakpointLine[];
  /** Their sum, or null when no space carries one. */
  total: number | null;
}

// The tenant's natural breakpoint for the year, and the spaces it's made of.
//
// A tenant may hold several leases and each may demise several areas — shop,
// patio, storage, mezzanine — so what they're measured against is the total
// across all of them. A space contributes nothing when its Space Type doesn't
// attract percentage rent, when it has no unit to take an area from, or when
// its lease states no breakpoint rate; if none of them contributes, the tenant
// has no percentage rent at all, which is null rather than zero — a $0
// breakpoint would read as "every dollar is over the line".
//
// The per-lease work is occupancyBreakpointYear, shared with the admin's
// occupancy card: a tenant querying their breakpoint and the property manager
// checking it must be looking at one implementation, not two.
export function tenantBreakpointDetail(
  tenant: Tenant,
  unitById: (unitId: string) => Unit | undefined,
  configuration: Pick<TospConfiguration, "percentageRentSpaceTypes">,
  year: number = new Date().getFullYear(),
): TenantBreakpoint {
  const lines: TenantBreakpointLine[] = [];
  let total: number | null = null;

  for (const occupancy of tenant.occupancies || []) {
    const lease = occupancyBreakpointYear(occupancy, unitById, configuration, year);
    if (lease.total === null) continue;
    total = (total ?? 0) + lease.total;
    lines.push(...lease.lines);
  }

  return { lines, total };
}

/** Just the figure, for callers that don't need the workings. */
export function tenantNaturalBreakpoint(
  tenant: Tenant,
  unitById: (unitId: string) => Unit | undefined,
  configuration: Pick<TospConfiguration, "percentageRentSpaceTypes">,
  year: number = new Date().getFullYear(),
): number | null {
  return tenantBreakpointDetail(tenant, unitById, configuration, year).total;
}

// How far through the breakpoint the year's sales have got. Null unless both
// figures are known and there's a breakpoint to be over — with no breakpoint
// there's no percentage to be at.
export function breakpointProgress(
  salesToDate: number | null,
  breakpoint: number | null,
): number | null {
  if (salesToDate === null || breakpoint === null || breakpoint <= 0) return null;
  return (salesToDate / breakpoint) * 100;
}

/** Where a breakpoint bar sits against the traffic lights the module uses. */
export type BreakpointTone = "red" | "amber" | "green";

/**
 * The traffic light for a percentage of the natural breakpoint.
 *
 * Under half is red, half to just under is amber, the breakpoint met is green.
 * Read the tenant's way round: reaching the breakpoint is the year going well,
 * whatever it means for the percentage rent that starts above it.
 *
 * Compared at the ONE decimal the bars display, so a figure reading 100.0%
 * cannot sit in an amber bar because it is 99.96 underneath.
 */
export function breakpointTone(percent: number): BreakpointTone {
  const shown = Number(percent.toFixed(1));
  if (shown >= 100) return "green";
  return shown >= 50 ? "amber" : "red";
}

// Months of the year still to be traded, this one included — it isn't over, so
// its sales are still to come. In August that's August through December: five.
export function monthsRemaining(asOf: Date = new Date()): number {
  return 12 - asOf.getMonth();
}

/** A calendar month, 1–12. */
export interface YearMonth {
  year: number;
  month: number;
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Month name for a 1–12 month number. */
export const monthName = (month: number): string => MONTH_NAMES[month - 1] || "";

/**
 * Sales for a month are due on this day of the month following it. A default
 * until the lease's reporting terms are recorded, at which point it comes off
 * the occupancy rather than from here.
 */
export const SALES_DUE_DAY = 10;

/**
 * Whole days past a due date: 0 on the day itself, 1 the day after, negative
 * while it's still ahead.
 *
 * Compared date to date rather than instant to instant. A due date is a day,
 * not a moment — measuring from `now` would make a month "a day late" at one
 * minute past midnight and "due today" at eleven the previous evening, and
 * two callers rounding that differently is how the banner and the queue came
 * to disagree about the same month.
 */
export function daysPastDue(dueOn: Date, now: Date): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(dueOn.getFullYear(), dueOn.getMonth(), dueOn.getDate());
  return Math.round((today.getTime() - due.getTime()) / 86_400_000);
}

/** The furthest-on month of those given, or null when there are none. */
export function latestMonth(months: YearMonth[]): YearMonth | null {
  return months.reduce<YearMonth | null>(
    (latest, month) =>
      !latest || month.year * 12 + month.month > latest.year * 12 + latest.month
        ? month
        : latest,
    null,
  );
}

export interface SubmissionSchedule {
  /** The month whose sales are next to be reported. */
  target: YearMonth;
  /** The first day those sales can be entered — the day after the month ends. */
  opensOn: Date;
  /** The day they're due. */
  dueOn: Date;
  /** Whether the target month is over, so its sales can be entered now. */
  open: boolean;
}

// When the next lot of sales can be entered, and when they're due.
//
// Sales are reported a month in arrears: a month's takings can't be totalled
// until it's over, so they're entered from the 1st of the following month and
// due on its due day. That's why the button can read "Add August now" through
// August and still be closed — August isn't finished, so there's no figure to
// report yet.
export function nextSubmission(
  now: Date,
  lastSubmitted: YearMonth | null,
  dueDay: number,
): SubmissionSchedule {
  // The month after whatever was last reported. With nothing reported at all,
  // the month just ended is the one owing.
  const target = lastSubmitted
    ? // `month` is 1-based and the constructor is 0-based, so this is already
      // the month after the one submitted.
      new Date(lastSubmitted.year, lastSubmitted.month, 1)
    : new Date(now.getFullYear(), now.getMonth() - 1, 1);

  // Rolls the year over on its own when the target is December.
  const opensOn = new Date(target.getFullYear(), target.getMonth() + 1, 1);
  const dueOn = new Date(target.getFullYear(), target.getMonth() + 1, dueDay);

  return {
    target: { year: target.getFullYear(), month: target.getMonth() + 1 },
    opensOn,
    dueOn,
    open: now >= opensOn,
  };
}
